<script setup>
import { computed, onMounted, ref } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { getUnixEndOfDay, getUnixStartOfDay } from 'helpers/DateHelper';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import WootDateRangePicker from 'dashboard/components/ui/DateRangePicker.vue';

const props = defineProps({
  initialSelectedAgents: {
    type: Array,
    default: () => [],
  },
  selectedRecordingCount: {
    type: Number,
    default: 0,
  },
  isDownloading: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['download']);

const { t } = useI18n();
const store = useStore();

const dialogRef = ref(null);
const initialFocusRef = ref(null);
const useSelectedRecordingsOnly = ref(false);
const useDateRange = ref(true);
const dateRange = ref([new Date(), new Date()]);
const selectedAgents = ref([]);

const agentOptions = computed(() => store.getters['agents/getAgents'] || []);
const isSubmitDisabled = computed(
  () =>
    props.isDownloading ||
    (useDateRange.value && (!dateRange.value?.[0] || !dateRange.value?.[1]))
);

const resetState = () => {
  useSelectedRecordingsOnly.value = false;
  useDateRange.value = false;
  dateRange.value = [new Date(), new Date()];
  selectedAgents.value = [...props.initialSelectedAgents];
};

const open = () => {
  resetState();
  dialogRef.value?.open();
  requestAnimationFrame(() => {
    initialFocusRef.value?.focus();
  });
};

const close = () => {
  dialogRef.value?.close();
};

const onConfirm = () => {
  emit('download', {
    useSelectedRecordingsOnly: useSelectedRecordingsOnly.value,
    from:
      useSelectedRecordingsOnly.value || !useDateRange.value
        ? null
        : getUnixStartOfDay(dateRange.value[0]),
    to:
      useSelectedRecordingsOnly.value || !useDateRange.value
        ? null
        : getUnixEndOfDay(dateRange.value[1]),
    agentIds: useSelectedRecordingsOnly.value
      ? []
      : selectedAgents.value.map(agent => agent.id),
  });
};

const onDateRangeChange = value => {
  dateRange.value = value;
};

onMounted(() => {
  store.dispatch('agents/get');
  resetState();
});

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    type="edit"
    width="2xl"
    :title="t('RECORDING_REPORTS.DOWNLOAD_DIALOG.TITLE')"
    :description="t('RECORDING_REPORTS.DOWNLOAD_DIALOG.DESCRIPTION')"
    :confirm-button-label="t('RECORDING_REPORTS.DOWNLOAD_DIALOG.CONFIRM')"
    :is-loading="isDownloading"
    :disable-confirm-button="isSubmitDisabled"
    @confirm="onConfirm"
  >
    <div class="flex flex-col gap-5">
      <div ref="initialFocusRef" tabindex="-1" />
      <div
        v-if="selectedRecordingCount"
        class="rounded-xl border border-n-strong bg-n-alpha-2 p-4"
      >
        <label class="flex items-start gap-3">
          <Checkbox v-model="useSelectedRecordingsOnly" class="mt-1" />
          <div class="flex flex-col gap-1">
            <span class="text-sm font-medium text-n-slate-12">
              {{
                t('RECORDING_REPORTS.DOWNLOAD_DIALOG.SELECTED_ONLY', {
                  count: selectedRecordingCount,
                })
              }}
            </span>
            <span class="text-sm text-n-slate-11">
              {{ t('RECORDING_REPORTS.DOWNLOAD_DIALOG.SELECTED_ONLY_HELP') }}
            </span>
          </div>
        </label>
      </div>

      <div v-if="!useSelectedRecordingsOnly" class="flex flex-col gap-5">
        <div class="flex flex-col gap-3">
          <label class="flex items-start gap-3">
            <Checkbox v-model="useDateRange" class="mt-1" />
            <div class="flex flex-col gap-1">
              <span class="text-sm font-medium text-n-slate-12">
                {{ t('RECORDING_REPORTS.DOWNLOAD_DIALOG.USE_DATE_RANGE') }}
              </span>
              <span class="text-sm text-n-slate-11">
                {{ t('RECORDING_REPORTS.DOWNLOAD_DIALOG.USE_DATE_RANGE_HELP') }}
              </span>
            </div>
          </label>

          <WootDateRangePicker
            v-if="useDateRange"
            :append-to-body="false"
            show-range
            class="no-margin auto-width"
            :value="dateRange"
            :confirm-text="$t('REPORT.CUSTOM_DATE_RANGE.CONFIRM')"
            :placeholder="$t('REPORT.CUSTOM_DATE_RANGE.PLACEHOLDER')"
            @change="onDateRangeChange"
          />
        </div>

        <div class="flex flex-col gap-2">
          <span class="text-sm font-medium text-n-slate-12">
            {{ t('RECORDING_REPORTS.DOWNLOAD_DIALOG.AGENTS_LABEL') }}
          </span>
          <multiselect
            v-model="selectedAgents"
            class="no-margin"
            :options="agentOptions"
            track-by="id"
            label="name"
            multiple
            :close-on-select="false"
            :clear-on-select="false"
            hide-selected
            :placeholder="t('RECORDING_REPORTS.FILTERS.AGENTS_PLACEHOLDER')"
            selected-label
            :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
            :deselect-label="$t('FORMS.MULTISELECT.ENTER_TO_REMOVE')"
          />
          <span class="text-sm text-n-slate-11">
            {{ t('RECORDING_REPORTS.DOWNLOAD_DIALOG.AGENTS_HELP') }}
          </span>
        </div>
      </div>
    </div>
  </Dialog>
</template>
